import React, { useEffect, useState } from "react";
import { analyticsAPI } from "../../../../services/analyticsAPI";


const InspectorPerformanceView = ({ projectId, onOpenInspection }) => {
  const [range, setRange] = useState({ start: "", end: "" });
  const [inspectorId, setInspectorId] = useState("");
  const [inspectionType, setInspectionType] = useState("");
  const [inspectorOptions, setInspectorOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [detailOffset, setDetailOffset] = useState(0);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailInspectorId, setDetailInspectorId] = useState(null);
  const [data, setData] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    setError("");
    analyticsAPI.inspectorPerformance(projectId, {
      ...(range.start ? { start: range.start } : {}),
      ...(range.end ? { end: range.end } : {}),
      ...(inspectorId ? { inspector_id: inspectorId } : {}),
      ...(inspectionType ? { inspection_type: inspectionType } : {}),
    }, ctrl.signal).then(({ data: next }) => {
      setData(next);
      setDetails([]); setDetailTotal(0); setDetailOffset(0);
      if (!inspectorId) setInspectorOptions(next.inspectors || []);
      if (!inspectionType) setTypeOptions([...new Set(
        (next.inspection_types || []).map(r => r.inspection_type).filter(Boolean))].sort());
    }).catch((e) => {
      if (e?.code !== "ERR_CANCELED") setError(e?.response?.data?.detail || "Failed to load inspector performance");
    }).finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [projectId, range.start, range.end, inspectorId, inspectionType]);

  const openDetails = async (id, offset = 0) => {
    setDetailInspectorId(id);
    setDetailOffset(offset);
    try {
      const { data: result } = await analyticsAPI.inspectorDetails(projectId, {
        start: range.start || data?.start,
        end: range.end || data?.end,
        inspector_id: id,
        inspection_type: inspectionType || undefined,
        offset, limit: 100,
      });
      setDetails(result.rows || []); setDetailTotal(result.total || 0);
    } catch (e) { setError(e?.response?.data?.detail || "Unable to load details"); }
  };

  const showHours = (stats) => stats?.average_hours == null
    ? "—"
    : `${stats.average_hours} h (median ${stats.median_hours ?? "—"} h, ${stats.observations ?? 0} measured)`;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-semibold">Inspector Performance</h3>
        <div className="flex gap-2 items-center flex-wrap text-sm">
          <label>From <input type="date" value={range.start || data?.start || ""}
            onChange={e => setRange(r => ({ ...r, start: e.target.value }))} className="border rounded p-2" /></label>
          <label>To <input type="date" value={range.end || data?.end || ""}
            onChange={e => setRange(r => ({ ...r, end: e.target.value }))} className="border rounded p-2" /></label>
          <select aria-label="Inspector" value={inspectorId} onChange={e => setInspectorId(e.target.value)} className="border rounded p-2">
            <option value="">All inspectors</option>
            {inspectorOptions.map(i => <option value={i.user_id} key={i.user_id}>{i.name}</option>)}
          </select>
          <select aria-label="Inspection type" value={inspectionType} onChange={e => setInspectionType(e.target.value)} className="border rounded p-2">
            <option value="">All inspection types</option>
            {typeOptions.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
      </div>
      <p className="text-xs text-slate-500">Project timezone: {data?.timezone || "loading"}. Period outcomes use event dates; pending is the current status.</p>
      {loading && <p role="status">Loading inspector metrics…</p>}
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {data && !loading && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.overview || {}).map(([key, value]) =>
            <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-xs text-slate-600">{key.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</div><strong className="text-xl text-slate-900">{value?.toLocaleString?.() ?? value}</strong></div>)}
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200"><table className="min-w-full text-sm border-collapse">
          <thead><tr>{["Inspector", "Created", "Submitted", "Approved", "Rejected", "Rejection Decisions", "Pending Review (Now)", "First-Pass Approval %", "Preparation Time", "Rework Time", "Details"].map(t =>
            <th key={t} className="text-left border-b bg-slate-100 text-slate-700 p-3 whitespace-nowrap">{t}</th>)}</tr></thead>
          <tbody>{(data.inspectors || []).map(i => <tr key={i.user_id}>
            {[i.name, i.created, i.submitted, i.approved, i.rejected, i.rejection_events,
              i.pending_review_now, i.first_pass_approval_rate == null ? "—" : `${i.first_pass_approval_rate}%`,
              showHours(i.preparation), showHours(i.rework)].map((v, k) =>
              <td key={k} className="p-3 border-b border-slate-100">{v}</td>)}
            <td className="p-2 border-b"><button className="text-orange-700 underline" onClick={() => openDetails(i.user_id)}>View</button></td>
          </tr>)}</tbody>
        </table></div>
        {details.length > 0 && <div className="border rounded p-4">
          <h4 className="font-semibold mb-2">Inspection details ({detailTotal} total)</h4>
          {details.map(i => <button key={i.inspection_id} className="block text-orange-700 underline my-1"
            onClick={() => onOpenInspection(i.inspection_id)}>
            #{i.inspection_number} — {i.status_now} — {i.type || "Unspecified"}
          </button>)}
          <div className="flex gap-3 items-center text-sm">
            <button disabled={detailOffset === 0} onClick={() => openDetails(detailInspectorId, Math.max(0, detailOffset - 100))}>Previous</button>
            <span>{detailOffset + 1}–{Math.min(detailTotal, detailOffset + details.length)} of {detailTotal}</span>
            <button disabled={detailOffset + details.length >= detailTotal} onClick={() => openDetails(detailInspectorId, detailOffset + 100)}>Next</button>
          </div>
        </div>}
      </>}
    </section>
  );
};

export default InspectorPerformanceView;
